import { Suspense, useState } from 'react';
import { useClerk, useUser } from '@clerk/clerk-react';
import { Check, LogOut } from 'lucide-react';
import { Outlet, useLocation, useOutletContext } from 'react-router';
import { Logo, mountainLineUrl } from '@bill-alps/ui';
import { useTranslation } from '../i18n/useTranslation';
import { LanguageSwitcher } from '@/components/language-switcher';
import { PageErrorBoundary } from '@/components/page-error-boundary';
import { PageLoader } from '@/components/page-loader';
import { AuthProgressContext, type AuthProgressStep } from './auth-progress';

function routeProgressStep(pathname: string): AuthProgressStep | null {
  if (pathname === '/welcome/subscribe') return 5;
  if (pathname === '/onboarding') return 2;
  if (pathname.startsWith('/sign-up')) return 1;
  return null;
}

function FloatingShapes() {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-0"
      aria-hidden="true"
    >
      <div
        className="auth-animate absolute right-[8%] top-[-70px] h-[200px] w-[200px] rounded-full bg-[var(--auth-yellow)] opacity-[0.13] animate-auth-float"
        style={{ animationDelay: '0s' }}
      />
      <div
        className="auth-animate absolute right-[-60px] top-[38%] h-[150px] w-[150px] rounded-full bg-[var(--auth-pink)] opacity-[0.12] animate-auth-float"
        style={{ animationDelay: '-2s' }}
      />
      <div
        className="auth-animate absolute bottom-[12%] left-[6%] h-24 w-24 rounded-full bg-[var(--auth-teal)] opacity-[0.13] animate-auth-float"
        style={{ animationDelay: '-4s' }}
      />
      <div
        className="auth-animate absolute bottom-[26%] right-[14%] h-[54px] w-[54px] rounded-2xl bg-[var(--auth-blue)] opacity-[0.14] animate-auth-float"
        style={{ animationDelay: '-1s' }}
      />
    </div>
  );
}

function ColoredDots() {
  const colors = [
    'var(--auth-pink)',
    'var(--auth-yellow)',
    'var(--auth-teal)',
    'var(--auth-blue)',
    'var(--auth-coral)',
  ];

  return (
    <div className="flex justify-center gap-2" aria-hidden="true">
      {colors.map((color, index) => (
        <span
          key={index}
          className="block size-2.5 rounded-full"
          style={{ background: color }}
        />
      ))}
    </div>
  );
}

function ProductBenefits() {
  const { t } = useTranslation('auth');

  return (
    <p
      className="relative text-center text-[15px] font-semibold leading-relaxed text-white/75"
      data-auth-benefits
    >
      {t('shell.benefit')}
    </p>
  );
}

function ProgressRail({ currentStep }: { currentStep: AuthProgressStep }) {
  const { t } = useTranslation('auth');
  const steps = [1, 2, 3, 4, 5] as const;

  return (
    <div className="relative">
      <div
        aria-hidden="true"
        className="absolute bottom-[26px] left-[15px] top-[26px] w-0.5 bg-white/15"
      />
      <ol
        className="relative flex flex-col"
        aria-label={t('shell.progressLabel')}
        data-auth-progress
      >
        {steps.map((step) => {
          const isComplete = step < currentStep;
          const isCurrent = step === currentStep;

          return (
            <li
              key={step}
              aria-current={isCurrent ? 'step' : undefined}
              className="relative flex items-center gap-3.5 py-2"
              data-auth-step={step}
            >
              {isComplete ? (
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--auth-teal)]">
                  <Check className="size-4 text-white" aria-hidden="true" />
                  <span className="sr-only">{t('shell.completed')}</span>
                </span>
              ) : (
                <span
                  className={`flex size-8 shrink-0 items-center justify-center rounded-full text-[13px] font-extrabold ${
                    isCurrent
                      ? 'bg-white text-[var(--auth-dark)] shadow-[0_0_0_5px_rgb(255_255_255_/_0.12)]'
                      : 'border-2 border-white/25 bg-[var(--auth-dark)] text-white/45'
                  }`}
                >
                  {step}
                </span>
              )}
              <span
                className={
                  isCurrent
                    ? 'text-base font-extrabold tracking-[-0.01em] text-white'
                    : 'text-[15px] font-semibold text-white/55'
                }
              >
                {t(`shell.steps.${step}`)}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function BrandPanel({ currentStep }: { currentStep: AuthProgressStep | null }) {
  const { t } = useTranslation('auth');

  return (
    <aside
      data-auth-sidebar
      className="relative hidden h-[var(--app-viewport-dvh)] min-w-[360px] max-w-[520px] basis-[38%] shrink-0 flex-col justify-between gap-10 overflow-x-hidden overflow-y-auto bg-[var(--auth-dark)] px-11 pb-[38px] pt-10 text-white max-xl:min-w-[320px] max-xl:max-w-[420px] max-xl:basis-[32%] max-xl:px-8"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-100"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgb(255 255 255 / 0.06) 1px, transparent 1px)',
            backgroundSize: '10px 10px',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute bottom-[-40px] left-[-60px] right-[-60px] h-[340px] bg-white opacity-[0.09]"
          style={{
            WebkitMask: `url(${mountainLineUrl}) no-repeat center bottom / cover`,
            mask: `url(${mountainLineUrl}) no-repeat center bottom / cover`,
          }}
        />
      </div>

      <div className="relative flex flex-col gap-[26px]">
        <Logo size="2.1rem" className="self-start !text-white" />
        <div className="flex flex-col gap-2">
          {currentStep ? (
            <>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">
                {t('shell.stepCounter', { current: currentStep, total: 5 })}
              </span>
              <h2 className="m-0 max-w-[14em] text-[26px] font-extrabold leading-[1.15] tracking-[-0.01em]">
                {t('shell.workspaceTitle')}
              </h2>
            </>
          ) : (
            <h2 className="m-0 max-w-[14em] text-[26px] font-extrabold leading-[1.15] tracking-[-0.01em]">
              {t('shell.panelTitle')}
            </h2>
          )}
        </div>
      </div>

      {currentStep && <ProgressRail currentStep={currentStep} />}
      <ProductBenefits />
    </aside>
  );
}

function MobileBrandHeader({
  currentStep,
}: {
  currentStep: AuthProgressStep | null;
}) {
  return (
    <div
      className="flex flex-col items-center gap-[18px]"
      data-auth-mobile-header
    >
      <Logo size="2.4rem" />
      {currentStep && (
        <div
          className="flex items-center gap-1.5"
          aria-hidden="true"
          data-auth-mobile-progress
        >
          {([1, 2, 3, 4, 5] as const).map((step) => (
            <span
              key={step}
              data-auth-mobile-step={step}
              data-current={step === currentStep ? 'true' : undefined}
              className={`h-1.5 rounded-full ${
                step < currentStep
                  ? 'w-[26px] bg-[var(--auth-teal)]'
                  : step === currentStep
                    ? 'w-[42px] bg-[var(--auth-blue)]'
                    : 'w-[26px] bg-[var(--auth-outline)]'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function AuthLayout() {
  const { t } = useTranslation(['auth', 'common']);
  const outletContext = useOutletContext();
  const location = useLocation();
  const { isSignedIn } = useUser();
  const { signOut } = useClerk();
  const [progressOverride, setProgressOverride] =
    useState<AuthProgressStep | null>(null);
  const defaultProgressStep = routeProgressStep(location.pathname);
  const isOnboarding = location.pathname === '/onboarding';
  const acceptsProgressOverride =
    isOnboarding ||
    (import.meta.env.DEV && location.pathname === '/dev/auth-layout');
  const currentStep = acceptsProgressOverride
    ? (progressOverride ?? defaultProgressStep)
    : defaultProgressStep;
  const isWide = location.pathname === '/welcome/subscribe';

  return (
    <AuthProgressContext.Provider value={setProgressOverride}>
      <div
        className="flex min-h-[var(--app-viewport-dvh)] overflow-x-hidden bg-[var(--auth-bg)] font-auth-body text-[var(--auth-dark)] min-[901px]:h-[var(--app-viewport-dvh)] min-[901px]:overflow-hidden"
        data-auth-layout
      >
        <BrandPanel currentStep={currentStep} />

        <main className="relative flex h-[var(--app-viewport-dvh)] min-h-[var(--app-viewport-dvh)] min-w-0 flex-1 flex-col items-center overflow-x-hidden overflow-y-auto px-5 pb-8 pt-5 min-[561px]:px-8">
          <FloatingShapes />

          <div
            className={`relative z-10 flex w-full justify-end gap-2 ${
              isWide ? 'max-w-6xl' : 'max-w-[600px]'
            }`}
          >
            <LanguageSwitcher appearance="auth" />
            {isSignedIn && (
              <button
                type="button"
                onClick={() => void signOut({ redirectUrl: '/' })}
                className="flex min-h-9 cursor-pointer items-center gap-1.5 rounded-full border-2 border-transparent bg-transparent px-3.5 py-1.5 text-[13px] font-bold text-[#6b7280] transition-colors hover:border-[var(--auth-outline)] hover:text-[var(--auth-dark)]"
              >
                <LogOut className="size-[15px]" aria-hidden="true" />
                {t('auth:shell.signOut')}
              </button>
            )}
          </div>

          <div
            data-auth-content
            className={`auth-animate relative z-[1] flex w-full flex-1 flex-col justify-center gap-[26px] py-6 animate-auth-fade-up ${
              isWide ? 'max-w-6xl' : 'max-w-[600px]'
            }`}
          >
            <MobileBrandHeader currentStep={currentStep} />

            <PageErrorBoundary key={location.pathname}>
              <Suspense fallback={<PageLoader />}>
                <Outlet context={outletContext} />
              </Suspense>
            </PageErrorBoundary>
          </div>

          <footer className="relative z-[1] flex flex-col items-center gap-3">
            <ColoredDots />
            <p className="m-0 text-center text-[11px] font-bold uppercase tracking-[2.5px] text-[#b0b0c0]">
              <a
                href="https://www.w-ap.ch"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-[#8a8a9a]"
              >
                {t('common:footerCompany')}
              </a>
              {' · '}
              {t('common:footerCity')}
            </p>
          </footer>
        </main>
      </div>
    </AuthProgressContext.Provider>
  );
}
